import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cx } from './cx.js';
/**
 * Layout primitives (§17.2). Composition-first: spacing lives in the layout
 * component, never sprinkled as margins on children, so a screen's rhythm is
 * declared in one place.
 */
const GAP = {
    none: 'gap-0',
    xs: 'gap-1',
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-5',
    xl: 'gap-8',
};
/** Vertical flow. */
export function Stack({ children, gap = 'md', className, as: Tag = 'div' }) {
    return _jsx(Tag, { className: cx('flex flex-col', GAP[gap], className), children: children });
}
/** Horizontal grouping that wraps rather than overflowing. */
export function Cluster({ children, gap = 'sm', align = 'center', justify = 'start', wrap = true, className, }) {
    const alignment = {
        start: 'items-start',
        center: 'items-center',
        end: 'items-end',
        baseline: 'items-baseline',
    }[align];
    const justification = {
        start: 'justify-start',
        center: 'justify-center',
        end: 'justify-end',
        between: 'justify-between',
    }[justify];
    return (_jsx("div", { className: cx('flex', wrap && 'flex-wrap', alignment, justification, GAP[gap], className), children: children }));
}
/** Responsive grid that reflows without breakpoint soup. */
export function Grid({ children, min = 'md', gap = 'md', className }) {
    const template = {
        sm: 'grid-cols-[repeat(auto-fill,minmax(min(100%,14rem),1fr))]',
        md: 'grid-cols-[repeat(auto-fill,minmax(min(100%,20rem),1fr))]',
        lg: 'grid-cols-[repeat(auto-fill,minmax(min(100%,28rem),1fr))]',
    }[min];
    return _jsx("div", { className: cx('grid', template, GAP[gap], className), children: children });
}
/**
 * Standard page frame (§17.2): an iconised heading, optional stat strip and
 * actions, then content. The header carries a subtle brand wash so a page reads
 * as a designed surface, not a bare panel.
 */
export function Page({ title, description, actions, icon: Icon, stats, children }) {
    return (_jsxs("div", { className: "mx-auto w-full max-w-7xl px-6 py-6", children: [_jsxs("div", { className: "mb-5 rounded-xl border border-edge bg-gradient-to-br from-surface to-surface-muted px-5 py-4", children: [_jsxs(Cluster, { justify: "between", align: "start", children: [_jsxs("div", { className: "flex items-start gap-3", children: [Icon !== undefined && (_jsx("span", { className: "mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-navy-800 text-gold-400", children: _jsx(Icon, { size: 20 }) })), _jsxs("div", { children: [_jsx("h1", { className: "font-heading text-2xl text-navy-700", children: title }), description !== undefined && (_jsx("p", { className: "mt-1 max-w-2xl text-sm text-ink-muted", children: description }))] })] }), actions !== undefined && _jsx(Cluster, { gap: "sm", children: actions })] }), stats !== undefined && _jsx("div", { className: "mt-4", children: stats })] }), children] }));
}
/**
 * Explicit, DESIGNED empty state (§17.2). An empty list must say it is empty and
 * still look intentional — a blank panel reads as a broken or unfinished screen.
 * The optional feature chips turn "nothing here yet" into a showcase of what the
 * surface is for.
 */
export function EmptyState({ title, description, action, icon: Icon, features }) {
    return (_jsxs("div", { className: "flex flex-col items-center rounded-xl border border-edge bg-gradient-to-b from-surface-muted to-surface px-6 py-12 text-center", children: [Icon !== undefined && (_jsx("span", { className: "mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gold-200 text-gold-600", children: _jsx(Icon, { size: 26 }) })), _jsx("p", { className: "font-heading text-lg text-navy-700", children: title }), description !== undefined && (_jsx("p", { className: "mx-auto mt-2 max-w-md text-sm text-ink-muted", children: description })), action !== undefined && (_jsx("div", { className: "mt-5 flex flex-wrap justify-center gap-2", children: action })), features !== undefined && features.length > 0 && (_jsx("ul", { className: "mt-8 grid w-full max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3", children: features.map((f) => (_jsxs("li", { className: "flex items-center gap-2.5 rounded-lg border border-edge bg-surface px-3 py-2.5 text-left", children: [_jsx("span", { className: "flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-navy-800 text-gold-400", children: _jsx(f.icon, { size: 16 }) }), _jsx("span", { className: "text-xs font-medium text-navy-700", children: f.label })] }, f.label))) }))] }));
}
//# sourceMappingURL=layout.js.map